
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.spinner = this.querySelector('.loading__spinner');

        this.cart =
          document.querySelector('cart-drawer') ||
          document.querySelector('cart-notification');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.error = false;
        this.isSubmitting = false;

        this.boundSubmitHandler = this.onSubmitHandler.bind(this);

        if (this.variantIdInput) {
          this.variantIdInput.disabled = false;
        }

        if (
          this.submitButton &&
          document.querySelector('cart-drawer')
        ) {
          this.submitButton.setAttribute(
            'aria-haspopup',
            'dialog'
          );
        }
      }

      connectedCallback() {
        if (!this.form) return;

        this.form.addEventListener(
          'submit',
          this.boundSubmitHandler
        );
      }

      disconnectedCallback() {
        if (!this.form) return;

        this.form.removeEventListener(
          'submit',
          this.boundSubmitHandler
        );
      }

      async onSubmitHandler(event) {
        event.preventDefault();

        if (
          this.isSubmitting ||
          !this.form ||
          !this.submitButton ||
          this.submitButton.disabled ||
          this.submitButton.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        this.isSubmitting = true;
        this.error = false;

        this.handleErrorMessage();

        this.setLoading(true);

        const formData = new FormData(this.form);

        if (!formData.get('id')) {
          console.error(
            'PT Pack: No product variant selected.'
          );

          this.handleErrorMessage(
            'Please select a product option.'
          );

          this.setLoading(false);
          this.isSubmitting = false;
          return;
        }

        const drawer = document.querySelector('cart-drawer');
        const notification = document.querySelector(
          'cart-notification'
        );

        this.cart = drawer || notification;

        const sectionsToRequest = this.cart
          ? this.cart.getSectionsToRender().map(
              (section) => section.id
            )
          : [
              'cart-drawer',
              'cart-icon-bubble'
            ];

        formData.set(
          'sections',
          sectionsToRequest.join(',')
        );

        formData.set(
          'sections_url',
          window.location.pathname
        );

        if (
          this.cart &&
          typeof this.cart.setActiveElement === 'function'
        ) {
          this.cart.setActiveElement(
            document.activeElement
          );
        }

        try {
          const response = await fetch(
            window.routes?.cart_add_url || '/cart/add.js',
            {
              method: 'POST',
              headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'Accept': 'application/json'
              },
              body: formData
            }
          );

          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(
              result.description ||
              result.message ||
              'Unable to add this product to your cart.'
            );
          }

          this.error = false;

          // Notify other Dawn components without allowing a
          // subscriber error to interrupt the cart drawer.

          try {
            if (
              typeof publish === 'function' &&
              typeof PUB_SUB_EVENTS !== 'undefined'
            ) {
              const published = publish(
                PUB_SUB_EVENTS.cartUpdate,
                {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: result
                }
              );

              if (
                published &&
                typeof published.catch === 'function'
              ) {
                published.catch((error) => {
                  console.warn(
                    'PT Pack: Cart subscriber warning',
                    error
                  );
                });
              }
            }
          } catch (error) {
            console.warn(
              'PT Pack: Cart update event warning',
              error
            );
          }

          // Refresh the cart UI after the product has
          // successfully been added.

          const quickAddModal = this.closest(
            'quick-add-modal'
          );

          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                this.refreshCart(result);
              },
              { once: true }
            );

            quickAddModal.hide(true);
          } else {
            await this.refreshCart(result);
          }

        } catch (error) {
          this.error = true;

          console.error(
            'PT Pack: Add to cart error:',
            error
          );

          this.handleErrorMessage(
            error.message ||
            'Unable to add this product. Please try again.'
          );

        } finally {
          this.setLoading(false);
          this.isSubmitting = false;
        }
      }

      async refreshCart(addResponse) {
        const drawer = document.querySelector(
          'cart-drawer'
        );

        const notification = document.querySelector(
          'cart-notification'
        );

        const cartComponent = drawer || notification;

        if (!cartComponent) {
          await this.updateCartIcon();
          return;
        }

        let sections = addResponse.sections || {};

        // If the Add to cart response did not include all
        // the required sections, request fresh HTML.

        const requiredSections = drawer
          ? ['cart-drawer', 'cart-icon-bubble']
          : cartComponent.getSectionsToRender().map(
              (section) => section.id
            );

        const missingSections = requiredSections.filter(
          (id) => !sections[id]
        );

        if (missingSections.length) {
          try {
            const fetchedSections =
              await this.fetchCartSections(
                missingSections
              );

            sections = {
              ...sections,
              ...fetchedSections
            };
          } catch (error) {
            console.warn(
              'PT Pack: Could not fetch cart sections',
              error
            );
          }
        }

        // Use Dawn's existing cart renderer first.

        if (
          requiredSections.every((id) => sections[id]) &&
          typeof cartComponent.renderContents === 'function'
        ) {
          try {
            cartComponent.renderContents({
              ...addResponse,
              sections
            });

            if (drawer) {
              drawer.classList.remove('is-empty');

              const inner = drawer.querySelector(
                '.drawer__inner'
              );

              inner?.classList.remove('is-empty');
            }

            return;
          } catch (error) {
            console.warn(
              'PT Pack: Standard drawer rendering failed. Using fallback.',
              error
            );
          }
        }

        // Fallback: replace the drawer and header sections
        // manually if Dawn's renderer could not complete.

        try {
          if (drawer && sections['cart-drawer']) {
            const parsed = new DOMParser().parseFromString(
              sections['cart-drawer'],
              'text/html'
            );

            const newDrawerContent = parsed.querySelector(
              '#CartDrawer'
            );

            const existingDrawerContent =
              drawer.querySelector('#CartDrawer');

            if (
              newDrawerContent &&
              existingDrawerContent
            ) {
              existingDrawerContent.innerHTML =
                newDrawerContent.innerHTML;
            }

            drawer.classList.remove('is-empty');

            drawer.querySelector(
              '.drawer__inner'
            )?.classList.remove('is-empty');

            const overlay = drawer.querySelector(
              '#CartDrawer-Overlay'
            );

            if (overlay) {
              overlay.addEventListener(
                'click',
                () => drawer.close()
              );
            }
          }

          if (sections['cart-icon-bubble']) {
            this.replaceCartIconSection(
              sections['cart-icon-bubble']
            );
          } else {
            await this.updateCartIcon();
          }

          if (
            drawer &&
            typeof drawer.open === 'function'
          ) {
            drawer.open();
          } else if (
            notification &&
            typeof notification.open === 'function'
          ) {
            notification.open();
          }

        } catch (error) {
          console.error(
            'PT Pack: Cart refresh fallback failed',
            error
          );

          // The product may already be in the cart.
          // Do not submit it again.

          await this.updateCartIcon();

          if (
            drawer &&
            typeof drawer.open === 'function'
          ) {
            drawer.open();
          }
        }
      }

      async fetchCartSections(sectionIds) {
        const root =
          window.Shopify?.routes?.root || '/';

        const url = new URL(
          root,
          window.location.origin
        );

        url.searchParams.set(
          'sections',
          sectionIds.join(',')
        );

        const response = await fetch(
          url.toString(),
          {
            headers: {
              'Accept': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(
            'Unable to fetch updated cart sections.'
          );
        }

        return await response.json();
      }

      replaceCartIconSection(html) {
        if (!html) return;

        const parsed = new DOMParser().parseFromString(
          html,
          'text/html'
        );

        const updatedIcon = parsed.querySelector(
          '#cart-icon-bubble'
        );

        const currentIcon = document.querySelector(
          '#cart-icon-bubble'
        );

        if (!updatedIcon || !currentIcon) return;

        // Preserve the existing icon element and its
        // event listeners, including Dawn's drawer opener.

        currentIcon.innerHTML = updatedIcon.innerHTML;
      }

      async updateCartIcon() {
        try {
          const root =
            window.Shopify?.routes?.root || '/';

          const cartResponse = await fetch(
            root + 'cart.js',
            {
              headers: {
                'Accept': 'application/json'
              }
            }
          );

          if (!cartResponse.ok) {
            throw new Error(
              'Unable to retrieve cart quantity.'
            );
          }

          const cart = await cartResponse.json();

          const cartIcon = document.querySelector(
            '#cart-icon-bubble'
          );

          if (!cartIcon) return;

          const count = Number(
            cart.item_count || 0
          );

          let bubble = cartIcon.querySelector(
            '.cart-count-bubble'
          );

          if (count <= 0) {
            bubble?.remove();
            return;
          }

          if (!bubble) {
            bubble = document.createElement('div');
            bubble.className = 'cart-count-bubble';
            cartIcon.appendChild(bubble);
          }

          bubble.innerHTML = `
            <span aria-hidden="true">${count}</span>
            <span class="visually-hidden">
              ${count} items
            </span>
          `;

        } catch (error) {
          console.error(
            'PT Pack: Cart icon update failed',
            error
          );
        }
      }

      setLoading(loading) {
        if (!this.submitButton) return;

        this.submitButton.classList.toggle(
          'loading',
          loading
        );

        this.submitButton.setAttribute(
          'aria-disabled',
          loading ? 'true' : 'false'
        );

        if (this.spinner) {
          this.spinner.classList.toggle(
            'hidden',
            !loading
          );
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (
          errorMessage &&
          this.errorMessage
        ) {
          this.errorMessage.textContent =
            errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute(
            'disabled',
            'disabled'
          );

          if (
            text &&
            this.submitButtonText
          ) {
            this.submitButtonText.textContent =
              text;
          }
        } else {
          this.submitButton.removeAttribute(
            'disabled'
          );

          if (this.submitButtonText) {
            this.submitButtonText.textContent =
              window.variantStrings?.addToCart ||
              'Add to cart';
          }
        }
      }

      get variantIdInput() {
        return this.form?.querySelector(
          '[name="id"]'
        );
      }
    }
  );
}
